/**
 * Extract a thumbnail frame from a video file or URL using <video> + canvas.
 * Returns a Promise<Blob> (PNG) of size (width x height) sampled at `timeSec`.
 *
 * Opts:
 *   timeSec  target seek time in seconds (default 1). Clamped to duration-0.1s.
 *   width    output canvas width in px (default 720)
 *   height   output canvas height in px (default 1280) — cover-fit
 *   mimeType output mime (default 'image/jpeg')
 *   quality  0..1 (default 0.85)
 */
export function extractVideoThumbnail(source, opts = {}) {
    const {
        timeSec = 1,
        width = 720,
        height = 1280,
        mimeType = "image/jpeg",
        quality = 0.85,
    } = opts;

    return new Promise((resolve, reject) => {
        const video = document.createElement("video");
        video.preload = "auto";
        video.muted = true;
        video.playsInline = true;

        let objectUrl = null;
        if (source instanceof Blob) {
            objectUrl = URL.createObjectURL(source);
            video.src = objectUrl;
        } else if (typeof source === "string") {
            // Cross-origin only applies to remote URLs; harmless to set for same-origin.
            video.crossOrigin = "anonymous";
            video.src = source;
        } else {
            reject(new Error("extractVideoThumbnail: unsupported source"));
            return;
        }

        const cleanup = () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };

        const onError = () => { cleanup(); reject(new Error("Failed to load video for thumbnail")); };

        video.addEventListener("error", onError, { once: true });

        video.addEventListener("loadedmetadata", () => {
            const seekTo = Math.min(Math.max(0.1, timeSec), Math.max(0.1, (video.duration || 1) - 0.1));
            // Some browsers require seeking after a play/pause cycle for accurate frames
            const seek = () => { try { video.currentTime = seekTo; } catch { /* ignore */ } };
            // Small delay so Chrome has a decoded frame
            setTimeout(seek, 50);
        }, { once: true });

        video.addEventListener("seeked", () => {
            try {
                const canvas = document.createElement("canvas");
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext("2d");
                // Cover-fit: scale video to fill canvas, crop overflow
                const vw = video.videoWidth || width;
                const vh = video.videoHeight || height;
                const scale = Math.max(width / vw, height / vh);
                const dw = vw * scale;
                const dh = vh * scale;
                const dx = (width - dw) / 2;
                const dy = (height - dh) / 2;
                ctx.fillStyle = "#000";
                ctx.fillRect(0, 0, width, height);
                ctx.drawImage(video, dx, dy, dw, dh);
                canvas.toBlob((blob) => {
                    cleanup();
                    if (!blob) return reject(new Error("toBlob returned null"));
                    resolve(blob);
                }, mimeType, quality);
            } catch (err) {
                cleanup();
                reject(err);
            }
        }, { once: true });
    });
}

/** Upload a Blob to /api/upload and return the full hosted URL. */
export async function uploadBlob(api, blob, filename = "thumbnail.jpg") {
    const form = new FormData();
    form.append("file", blob, filename);
    const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
    return `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
}
